import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { ASC } from 'app/shared/util/pagination.constants';
import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IEffect, defaultValue } from 'app/shared/model/effect.model';

const initialState: EntityState<IEffect> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  updateSuccess: false,
};

const apiUrl = 'api/effects';

// Actions

export const getEntities = createAsyncThunk(
  'effect/fetch_entity_list',
  async ({ page, size, sort, datasetId }: IQueryParams & { datasetId: string | number }) => {
    const requestUrl = `${apiUrl}?datasetId=${datasetId}&cacheBuster=${new Date().getTime()}`;
    return axios.get<IEffect[]>(requestUrl);
  },
);

export const getEntity = createAsyncThunk(
  'effect/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IEffect>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'effect/create_entity',
  async (entity: IEffect, thunkAPI) => {
    const result = await axios.post<IEffect>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({ datasetId: entity.dataset.id }));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'effect/update_entity',
  async (entity: IEffect, thunkAPI) => {
    const result = await axios.put<IEffect>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({ datasetId: entity.dataset.id }));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const partialUpdateEntity = createAsyncThunk(
  'effect/partial_update_entity',
  async (entity: IEffect, thunkAPI) => {
    const result = await axios.patch<IEffect>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({ datasetId: entity.dataset.id }));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
  'effect/delete_entity',
  async ({ id, datasetId }: { id: string | number; datasetId: number }, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IEffect>(requestUrl);
    thunkAPI.dispatch(getEntities({ datasetId }));
    return result;
  },
  { serializeError: serializeAxiosError },
);

// slice

export const EffectSlice = createEntitySlice({
  name: 'effect',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data } = action.payload;

        return {
          ...state,
          loading: false,
          entities: data.sort((a, b) => {
            if (!action.meta?.arg?.sort) {
              return 1;
            }
            const order = action.meta.arg.sort.split(',')[1];
            const predicate = action.meta.arg.sort.split(',')[0];
            return order === ASC ? (a[predicate] < b[predicate] ? -1 : 1) : b[predicate] < a[predicate] ? -1 : 1;
          }),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = EffectSlice.actions;

// Reducer
export default EffectSlice.reducer;
